<template>
  <div class="full-screen-search">
    <div class="close-button" @click="$emit('close')">
      <i class="fas fa-times"></i>
    </div>
    <div class="search-box" ref="searchContainer">
      <i class="fas fa-search search-icon"></i>
      <input
        type="text"
        class="search-input"
        placeholder="Recherche une destination"
        v-model="search"
        @input="fetchSuggestions"
        @focus="showSuggestions = true"
      />
    </div>
    <div v-if="showSuggestions" class="suggestions-dropdown">
      <template v-for="(section, key) in suggestions">
        <div v-if="section.length" class="suggestion-group">
          <div class="suggestion-header">{{ sectionTitles[key] }}</div>
          <div
            v-for="(item, index) in section"
            :key="item.id"
            class="suggestion-card-wrapper"
          >
            <div class="suggestion-card" @click.stop="handleSuggestionClick(key, item)">
              {{ item.name }}
            </div>
            <div v-if="index !== section.length - 1" class="suggestion-divider"></div>
          </div>
        </div>
      </template>
    </div>
    <div class="date-section">
      <div class="date-field" @click="openCalendar">
        <i class="fas fa-calendar-alt"></i>
        <div class="date-info">
          <div class="label">ARRIVÉE</div>
          <div class="value">{{ arrivalDate }}</div>
        </div>
      </div>
      <div class="divider"></div>
      <div class="date-field" @click="openCalendar">
        <i class="fas fa-calendar-alt"></i>
        <div class="date-info">
          <div class="label">DÉPART</div>
          <div class="value">{{ departureDate }}</div>
        </div>
      </div>
    </div>
    <div v-if="showCalendar" ref="datePickerContainer">
      <CommonDatePicker
          :startingDateValue="new Date(selectedStartDate)"
          :endingDateValue="new Date(selectedEndDate)"
          ref="datePicker"
          @periodSelected="selectDates"
          @datepickerClosed="closeDatepicker()"
          @datesCleared="clearDates"
      />
  </div>
    <div class="traveler-section">
      <div class="traveler-title">
        Nombres de voyageurs
        <i class="fas fa-chevron-up"></i>
      </div>
      <div class="traveler-field">
        <span>Adulte</span>
        <div class="counter">
          <button @click="decrease('adults')" :disabled="adults <= 1">-</button>
          <span>{{ adults }}</span>
          <button @click="increase('adults')">+</button>
        </div>
      </div>
      <div class="line"></div>
      <div class="traveler-field">
        <span>Enfants</span>
        <div class="counter">
          <button @click="decrease('children')" :disabled="children <= 0">-</button>
          <span>{{ children }}</span>
          <button @click="increase('children')">+</button>
        </div>
      </div>
      <div class="line" v-if="children"></div>
      <div class="dropdown-age child-age-selector" v-if="children">
        <p class="age-label raleway-reg-p15 pt-2">
          Quel est l’âge {{ formatName(children) }} voyageant avec vous ?
        </p>
        <v-select
          v-for="(child, index) in children"
          :key="`fullscreen-child-age-${index}`"
          class="select-item mb-1"
          placeholder="age"
          v-model="ageSelected[index]"
          :options="agesChoices"
          @input="selectAges(index, $event)"
        ></v-select>
      </div>
      <p
        v-if="showAgeError && children && hasMissingChildAges()"
        class="text-danger age-error text-center raleway-medi-p12"
      >
        Merci de nous indiquer l'âge de vos enfants
      </p>
    </div>
    <button @click="performSearch" class="search-button">RECHERCHER</button>
  </div>
</template>

<script>
import fetcher from 'packs/shared/api.js';
import {VueDatePicker} from "@mathieustan/vue-datepicker";
import CommonDatePicker from './CommonDatePicker.vue';
import _ from 'lodash';

export default {
  name: 'FullScreenSearch',
  components: {
    CommonDatePicker
  },
  data() {
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);
    return {
      selectedDates: [],
      currentDate: today,
      search: '',
      arrivalDate: this.formatDate(today),
      departureDate: this.formatDate(tomorrow),
      selectedStartDate: null,
      selectedEndDate: null,
      adults: 2,
      children: 0,
      ageSelected: [],
      agesChoices: [
        '1 an', '2 ans', '3 ans', '4 ans', '5 ans', '6 ans',
        '7 ans', '8 ans', '9 ans', '10 ans', '11 ans', '12 ans',
        '13 ans', '14 ans', '15 ans', '16 ans', '17 ans'
      ],
      showAgeError: false,
      isHydratingReservationState: true,
      showSuggestions: false,
      showCalendar: false,
      suggestions: {
        lodgings: [],
        departments: [],
        regions: [],
        cities: [],
        zones: [],
        territoires: []
      },
      sectionTitles: {
        lodgings: 'Hébergements',
        departments: 'Départements',
        regions: 'Régions',
        cities: 'Villes',
        zones: 'Destinations',
        territoires: 'Destinations'
      }
    };
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    
    // Create debounced version of the API call
    this.debouncedFetchSuggestions = _.debounce(async () => {
      try {
        const queryParams = new URLSearchParams({ query: this.search, geo: '48.8575,2.3514' });
        const filters = {
          destination: this.destinationPath,
          collection: this.collectionPath,
          weekend: this.weekendsPath,
          territoire: this.territoirePath,
          hotel: window.location.pathname === "/hotel-de-charme" ? true : null,
          home: window.location.pathname === "/les-nouveautes-hortense" ? true : null
        };
        for (const [key, value] of Object.entries(filters)) {
          if (value) queryParams.append(key, value);
        }
        const response = await fetcher.get(`/lodgings/search_suggestions?${queryParams.toString()}`);
        this.suggestions = response.data;
        this.showSuggestions = true;
      } catch (error) {
        console.error('Error fetching suggestions', error);
      }
    }, 500);
    
    const state = localStorage.getItem('reservationState');
    if (state) {
      const reservationState = JSON.parse(state);
      if (reservationState.adults !== undefined && reservationState.adults !== null) {
        this.adults = parseInt(reservationState.adults, 10);
      }
      if (reservationState.childs !== undefined && reservationState.childs !== null) {
        this.children = parseInt(reservationState.childs, 10);
      }
      if (Array.isArray(reservationState.children_ages)) {
        this.ageSelected = this.normalizeChildAges(reservationState.children_ages);
      }
      if (reservationState.from && reservationState.to) {
        this.selectedDates = [
          reservationState.koediaFrom || reservationState.from,
          reservationState.koediaTo || reservationState.to
        ];
        this.arrivalDate = this.formatDate2(new Date(reservationState.from));
        this.departureDate = this.formatDate2(new Date(reservationState.to));
        this.selectedStartDate = reservationState.koediaFrom || reservationState.from;
        this.selectedEndDate = reservationState.koediaTo || reservationState.to;
      };
    }

    this.$nextTick(() => {
      this.isHydratingReservationState = false;
    });

    const whatsappBtn = document.getElementById('whatsapp-button');
    if (whatsappBtn) {
      whatsappBtn.style.display = 'none';
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    // Cancel any pending debounced calls
    if (this.debouncedFetchSuggestions) {
      this.debouncedFetchSuggestions.cancel();
    }
    const whatsappBtn = document.getElementById('whatsapp-button');
    if (whatsappBtn) {
      whatsappBtn.style.display = 'block';
    }
  },
  computed: {
    minDate() {
      return new Date(
          this.currentDate.getFullYear(),
          this.currentDate.getMonth(),
          this.currentDate.getDate()
      );
    },
    maxDate() {
      return new Date(
          this.currentDate.getFullYear() + 1,
          this.currentDate.getMonth(),
          this.currentDate.getDate()
      );
    },
    destinationPath() {
      return window.location.pathname.startsWith('/destination/')
        ? window.location.pathname.split('/')[2] : null
    },
    collectionPath() {
      return window.location.pathname.startsWith('/collections/')
        ? window.location.pathname.split('/')[2] : null
    },
    weekendsPath() {
      return window.location.pathname.startsWith('/weekends/')
        ? window.location.pathname.split('/')[2] : null
    },
    territoirePath() {
      const params = new URLSearchParams(window.location.search);
      const territory = params.get('territoire');
      return window.location.pathname.startsWith('/reservation-hotel/') && territory
        ? territory : null
    }
  },
  methods: {
    getReservationState() {
      return JSON.parse(localStorage.getItem('reservationState') || '{}');
    },
    persistReservationState(partialState = {}) {
      const reservationState = {
        ...this.getReservationState(),
        ...partialState,
        adults: this.adults,
        childs: this.children,
        children_ages: this.normalizeChildAges(this.ageSelected),
      };

      localStorage.setItem('reservationState', JSON.stringify(reservationState));
    },
    fetchSuggestions() {
      if (this.search.length < 3) {
        this.resetSuggestions();
        this.showSuggestions = false;
        return;
      }
      // Call the debounced function
      this.debouncedFetchSuggestions();
    },

    performSearch(noDates = false) {
      if (this.selectedDates.length == 0 && noDates == false) {
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1)
        this.selectDates(null, today, tomorrow);
      }
      const trimmedSearch = this.search ? this.search.trim() : '';
      const path = window.location.pathname;
      
      // Save search state to localStorage instead of URL params
      const reservationState = {
        search: trimmedSearch,
        childs: this.children,
        adults: this.adults,
        children_ages: this.normalizeChildAges(this.ageSelected),
        koediaFrom: this.selectedDates ? this.formatKoediaDate(this.selectedDates[0]) : null,
        koediaTo: this.selectedDates ? this.formatKoediaDate(this.selectedDates[1]) : null,
        from: this.selectedDates ? this.formatKoediaDate(this.selectedDates[0]) : null,
        to: this.selectedDates ? this.formatKoediaDate(this.selectedDates[1]) : null,
        base: path.includes("/vacances-printemps") ? 'spring' : path.includes("/vacances-ete")  ? 'summer' : ''
      };
      if (!this.persistReservationState(reservationState)) {
        return;
      }
      
      window.dataLayer = window.dataLayer || [];
      let bookingWindow = null;

      if (this.selectedDates && this.selectedDates[0]) {
        const dateInParts = this.selectedDates[0].split('/');
        const dateIn = new Date(`${dateInParts[2]}-${dateInParts[1]}-${dateInParts[0]}`);
        const today = new Date();
        const timeDifference = dateIn - today;
        bookingWindow = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
      }
      const currentPath = window.location.pathname.startsWith('/')
        ? window.location.pathname
        : `/${window.location.pathname}`;
      
      // Redirect to eco-responsible events collection if URL includes evenements-prives
      if (currentPath.endsWith('/evenements-prives')) {
        window.location.href = '/collections/evenement-eco-responsable';
      } else {
        window.location.href = currentPath.includes('/voyage-sur-mesure') ? `/reservation-hotel` : currentPath;
      }
     },
    resetSuggestions() {
      this.suggestions = { lodgings: [], departments: [], regions: [], cities: [], zones: [], territoires: [] };
    },
    formatKoediaDate(date) {
          if (date) {
            if (date.includes('-') || !date) {
              return date;
            }
            const dateBits = date.split('/');
            return `${dateBits[2]}-${dateBits[1]}-${dateBits[0]}`;
          } else {
          return null;
          }
      },
    handleClickOutside(event) {
      if (this.$refs.searchContainer && !this.$refs.searchContainer.contains(event.target)) {
        this.showSuggestions = false;
      }
    },
    selectDates(event, start, end) {
      this.selectedDates = [this.formatDate3(start), this.formatDate3(end)];
      const state = {};
      if (this.selectedDates && this.selectedDates[0] && this.selectedDates[1]) {
        state.koediaFrom = this.formatKoediaDate(this.selectedDates[0]);
        state.koediaTo = this.formatKoediaDate(this.selectedDates[1]);
        state.from = this.formatKoediaDate(this.selectedDates[0]);
        state.to = this.formatKoediaDate(this.selectedDates[1]);
      }
      this.persistReservationState(state);
      if (start && end) {
          this.arrivalDate = this.formatDate2(start);
          this.departureDate = this.formatDate2(end);
      }
      this.selectedStartDate = start;
      this.selectedEndDate = end;
      this.closeDatepicker();
    },
    clearDates() {
      this.selectedDates = [];
      this.arrivalDate = this.formatDate(new Date());
      const tomorrow = new Date();
      tomorrow.setDate(new Date().getDate() + 1);
      this.departureDate = this.formatDate(tomorrow);
      this.selectedStartDate = null;
      this.selectedEndDate = null;
      const state = {
        koediaFrom: null,
        koediaTo: null,
        from: null,
        to: null,
        adults: this.adults,
        childs: this.children,
        children_ages: this.normalizeChildAges(this.ageSelected),
      };
      this.persistReservationState(state);
      this.performSearch(true);
    },
    closeDatepicker() {
      this.showCalendar = false;
      if (this.$refs.datePicker) {
        this.$refs.datePicker.closeDatepicker();
      }
    },
    formatDate2(date) {
      if (!date) return '';
      
      const monthsFR = [
          'JANV.', 'FEV.', 'MARS', 'AVR.', 'MAI', 'JUIN',
          'JUIL.', 'AOÛT', 'SEPT.', 'OCT.', 'NOV.', 'DEC.'
      ];
      
      const day = date.getDate();
      const month = monthsFR[date.getMonth()];
      const year = date.getFullYear();

      return `${day} ${month} ${year}`;
      },
      formatDate3(date) {
      if (!date) return null;
      const set = new Date(date);
      const options = { year: "numeric", month: "numeric", day: "numeric" };
      return set.toLocaleString("fr", options);
    },

    handleSuggestionClick(type, item) {
      this.search = item.name;
      this.showSuggestions = false;
      // if (type === 'lodgings') {
      //   window.open(`/reservation-hotel/${item.slug}`, '_blank');
      // } else if (['departments', 'regions', 'cities'].includes(type)) {
      //   window.open(`/destination/${item.slug}`, '_blank');
      // } else if (type === 'zones') {
      //   window.open(`/reservation-hotel?zone=${item.id}`, '_blank');
      // } else if (type === 'territoires') {
      //   window.open(`/reservation-hotel?territoire=${item.id}`, '_blank');
      // }
    },
    openCalendar() {
      this.showCalendar = true;
      this.$nextTick(() => {
        if (this.$refs.datePicker) {
          this.$refs.datePicker.showDatepicker();
        }
      });
    },
    updateDates({ start, end }) {
      this.selectedStartDate = start;
      this.selectedEndDate = end;
      this.arrivalDate = this.formatDate(start);
      this.departureDate = this.formatDate(end);
      this.showCalendar = false;
    },
    formatDate(date) {
      if (!date) return '';
      const options = { day: 'numeric', month: 'short', year: 'numeric' };
      return date.toLocaleDateString('fr-FR', options).toUpperCase().replace(/\./g, '');
    },
    increase(type) {
      this[type]++;
      if (type === 'children') {
        this.showAgeError = this.hasMissingChildAges();
      }
    },
    decrease(type) {
      if (this[type] > 0) this[type]--;
      if (type === 'children') {
        this.showAgeError = this.hasMissingChildAges();
      }
    },
    selectAges(index, value) {
      this.ageSelected[index] = value;
      this.showAgeError = this.hasMissingChildAges();
      this.persistReservationState();
    },
    formatName(count) {
      return count > 1 ? 'des enfants' : "de l'enfant";
    },
    hasMissingChildAges() {
      if (!this.children) return false;
      return this.ageSelected.slice(0, this.children).some((age) => !age);
    },
    normalizeChildAges(ages) {
      if (!Array.isArray(ages)) return [];
      return ages
        .map((age) => age?.toString?.().trim())
        .filter((age) => Boolean(age));
    },
    persistReservationState(partialState = {}) {
      if (this.children > 0 && this.hasMissingChildAges()) {
        this.showAgeError = true;
        return false;
      }

      this.showAgeError = false;
      const state = localStorage.getItem('reservationState');
      const reservationState = state ? JSON.parse(state) : {};
      localStorage.setItem(
        'reservationState',
        JSON.stringify({
          ...reservationState,
          ...partialState,
          adults: this.adults,
          childs: this.children,
          children_ages: this.normalizeChildAges(this.ageSelected),
        })
      );
      return true;
    }
  },
  watch: {
    adults: {
      handler(newVal) {
        if (this.isHydratingReservationState) return;
        if (newVal > 0) {
          this.persistReservationState({ adults: newVal });
        }
      },
      immediate: true,
    },
    children: {
      handler(newVal) {
        if (newVal > this.ageSelected.length) {
          this.ageSelected.push(...new Array(newVal - this.ageSelected.length).fill(null));
        } else {
          this.ageSelected = this.ageSelected.slice(0, newVal);
        }

        this.showAgeError = this.hasMissingChildAges();
        if (this.isHydratingReservationState) return;

        this.persistReservationState({
          childs: newVal,
          children_ages: this.normalizeChildAges(this.ageSelected),
        });
      },
      immediate: true,
    },
  }
};
</script>

<style scoped>
.full-screen-search {
position: fixed;
top: 0;
left: 0;
width: 100vw;
height: 100vh;
background: #fff;
z-index: 9999;
padding: 20px;
display: flex;
flex-direction: column;
overflow-y: auto;
font-family: 'Raleway', sans-serif;
}

.close-button {
width: 32px;
height: 32px;
background: #ddd;
border-radius: 8px;
display: flex;
align-items: center;
justify-content: center;
cursor: pointer;
}

.search-box {
display: flex;
align-items: center;
margin-top: 20px;
padding: 12px;
background: #f9f9f9;
border-radius: 4px;
border: 1px solid transparent;
}

.search-icon {
margin-right: 10px;
color: #666;
}

.search-input {
flex: 1;
border: none;
outline: none;
background: transparent;
font-size: 16px;
}

.suggestions-dropdown {
background: #f9f9f9;
border-radius: 8px;
margin-top: 8px;
box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
max-height: 400px;
padding: 15px 0;
overflow-y: hidden;
font-family: Raleway;
overflow-y: auto;
}

.suggestion-group {
margin-bottom: 20px;
}

.suggestion-header {
font-weight: bold;
font-size: 14px;
color: #3b6152;
padding: 10px 20px 5px;
text-transform: uppercase;
background: transparent;
font-family: Raleway;
}

.suggestion-card-wrapper {
padding: 0 20px;
}

.suggestion-card {
font-size: 16px;
color: #3b6152;
cursor: pointer;
padding: 10px 0;
font-family: Raleway;
}

.suggestion-card:hover {
background: #eef3f0;
}

.suggestion-divider {
width: 100%;
height: 1px;
background: #3b6152;
}

.date-section {
display: flex;
align-items: center;
background: #f9f9f9;
border-radius: 4px;
margin-top: 20px;
padding: 15px 10px;
}

.date-field {
display: flex;
flex-direction: column;
align-items: flex-start;
flex: 1;
cursor: pointer;
}

.date-field i {
margin-bottom: 5px;
font-size: 18px;
}

.divider {
width: 1px;
height: 50px;
background: #000;
margin: 0 10px;
}

.date-info .label {
font-size: 11px;
color: #333;
margin-bottom: 3px;
font-weight: 600;
}

.date-info .value {
font-size: 18px;
font-weight: bold;
color: #000;
}

.traveler-section {
margin-top: 20px;
background: #f9f9f9;
border-radius: 4px;
padding: 20px;
}

.traveler-title {
font-weight: bold;
font-size: 16px;
margin-bottom: 20px;
display: flex;
justify-content: space-between;
}

.traveler-field {
display: flex;
justify-content: space-between;
align-items: center;
padding: 12px 0;
font-size: 16px;
}

.line {
width: 100%;
height: 1px;
background: #ccc;
}

.counter {
display: flex;
align-items: center;
}

.counter button {
width: 36px;
height: 36px;
background: #fff;
border: 1px solid #000;
font-size: 20px;
font-weight: bold;
cursor: pointer;
color: #3b6152 !important;
}

.counter span {
margin: 0 15px;
font-size: 18px;
font-weight: bold;
}

.search-button {
margin-top: 6vh;
background: #3b6152;
color: white;
border: none;
width: 100%;
padding: 18px;
font-weight: bold;
font-size: 16px;
border-radius: 4px;
cursor: pointer;
}

</style>
